import { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Container, Dropdown, DropdownButton, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import NavbarComponent from "./Nav";
import { useAuth } from "./store/AuthContext";
import AdjudicationEvent from "./UHSMTAfiles/adjudicationEvent";
import CoachingEvent from "./UHSMTAfiles/coachingEvent";
import AwardsEventUHSMTA from "./UHSMTAfiles/AwardsEvent";
import WorkshopEvent from "./UHSMTAfiles/workshopEvent";
import InternEvent from "./UHSMTAfiles/internEvent";
import WinnersEvent from "./UHSMTAfiles/winnersEvent";
import SpecialPerformanceEvent from "./UHSMTAfiles/specialAwardsEvent";

const UHSMTA_MODULES = [
  { id: "adjudication", label: "Adjudication", group: "Event Types", Component: AdjudicationEvent },
  { id: "coaching", label: "Coaching", group: "Event Types", Component: CoachingEvent },
  { id: "workshops", label: "Workshops", group: "Event Types", Component: WorkshopEvent },
  { id: "specialPerformance", label: "Special Performance Event", group: "Event Types", Component: SpecialPerformanceEvent },
  { id: "winners", label: "Winners Event", group: "Event Types", Component: WinnersEvent },
  { id: "interns", label: "Interns", group: "Event Types", Component: InternEvent },
  { id: "awardsShow", label: "Awards Event UHSMTA", group: "Event Types", Component: AwardsEventUHSMTA },
];

const UHSMTAMainPage = () => {
  const { authState } = useAuth();
  const navigate = useNavigate();
  const [activeModuleId, setActiveModuleId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [recentModuleIds, setRecentModuleIds] = useState([]);

  useEffect(() => {
    if (!authState) {
      navigate("/login");
    }
  }, [authState, navigate]);

  const filteredModules = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return UHSMTA_MODULES;
    return UHSMTA_MODULES.filter((module) => module.label.toLowerCase().includes(query));
  }, [searchQuery]);

  const groupedModules = useMemo(() => {
    return filteredModules.reduce((acc, module) => {
      if (!acc[module.group]) acc[module.group] = [];
      acc[module.group].push(module);
      return acc;
    }, {});
  }, [filteredModules]);

  const activeModule = useMemo(
    () => UHSMTA_MODULES.find((module) => module.id === activeModuleId) || null,
    [activeModuleId]
  );

  const handleModuleSelect = (moduleId) => {
    setActiveModuleId((prev) => (prev === moduleId ? null : moduleId));
    setRecentModuleIds((prev) => [moduleId, ...prev.filter((id) => id !== moduleId)].slice(0, 3));
  };

  const ActiveComponent = activeModule?.Component;
  const recentModules = recentModuleIds
    .map((id) => UHSMTA_MODULES.find((module) => module.id === id))
    .filter(Boolean);

  return (
    <div>
      <NavbarComponent />

      {authState ? (
        <Container fluid className="py-3">
          <div className="management-button-list mb-3">
            <Form.Group controlId="uhsmtaSearch" style={{ minWidth: "18rem" }}>
              <Form.Label className="fw-bold">Find Event Type</Form.Label>
              <Form.Control
                type="text"
                placeholder="Search UHSMTA modules..."
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
            </Form.Group>
          </div>

          <div className="management-button-list">
            {Object.entries(groupedModules).map(([groupName, modules]) => (
              <DropdownButton key={groupName} id={`uhsmta-${groupName}`} title={groupName}>
                {modules.map((module) => (
                  <Dropdown.Item
                    as="button"
                    key={module.id}
                    onClick={() => handleModuleSelect(module.id)}
                    className="d-flex justify-content-between align-items-center"
                  >
                    <span>{module.label}</span>
                    {activeModuleId === module.id && <Badge bg="primary">Active</Badge>}
                  </Dropdown.Item>
                ))}
              </DropdownButton>
            ))}
          </div>

          {recentModules.length > 0 && (
            <div className="d-flex flex-wrap gap-2 my-3 align-items-center">
              <span className="fw-bold">Recent:</span>
              {recentModules.map((module) => (
                <Button
                  key={module.id}
                  size="sm"
                  variant={activeModuleId === module.id ? "primary" : "outline-primary"}
                  onClick={() => handleModuleSelect(module.id)}
                >
                  {module.label}
                </Button>
              ))}
            </div>
          )}

          <Card className="shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <strong>{activeModule ? `Active: ${activeModule.label}` : "UHSMTA Workspace"}</strong>
              {activeModule && (
                <Button variant="outline-danger" size="sm" onClick={() => setActiveModuleId(null)}>
                  Close
                </Button>
              )}
            </Card.Header>
            <Card.Body>
              {ActiveComponent ? (
                <ActiveComponent />
              ) : (
                <Alert variant="info" className="mb-0">
                  Select a UHSMTA module to open it here.
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Container>
      ) : (
        <div>
          <h1>Please refresh the page and login!</h1>
        </div>
      )}
    </div>
  );
};

export default UHSMTAMainPage;
